import { useEffect, useState } from 'react'
import { useMixpanel } from '@captaincause/lib/mixpanel'
import type { Project, ProjectId } from '@captaincause/types'

interface usePanelActionsProps {
  selectedProject: ProjectId
  projects: Project[]
  projectIdList?: ProjectId[]
  mixpanelPayload?: { [key: string]: any }
  onSeeMoreClick?: (id: ProjectId) => void
}

function pushProjectToUrl(projectId: ProjectId | undefined) {
  const url = new URL(window.location.href)
  if (projectId) {
    url.searchParams.set('project', projectId)
    window.history.pushState({}, '', url)
  }
}

const noop = () => {}
export function usePanelActions({
  selectedProject,
  projects,
  mixpanelPayload = {},
  projectIdList,
  onSeeMoreClick = noop,
}: usePanelActionsProps) {
  const { track } = useMixpanel()
  const [projectId, setProjectId] = useState<ProjectId | undefined>(
    selectedProject,
  )

  const close = () => {
    setProjectId(undefined)
    const url = new URL(window.location.href)
    url.searchParams.delete('project')
    window.history.pushState({}, '', url)
  }

  const seeMore = (id: ProjectId | undefined) => {
    setProjectId(id)
    pushProjectToUrl(id)
    if (id !== undefined) {
      track('Click on project card', { project_id: id, ...mixpanelPayload })
      onSeeMoreClick(id)
    }
  }

  const switchProject = (direction: 'left' | 'right') => {
    function getId(prev: string | undefined) {
      if (!projectIdList) return undefined
      const currentIndex = projectIdList.indexOf(prev || '')
      const nextIndex = currentIndex + (direction === 'left' ? -1 : 1)
      if (nextIndex < 0) {
        return projectIdList[projectIdList.length - 1]
      }
      if (nextIndex >= projectIdList.length) {
        return projectIdList[0]
      }

      return projectIdList[nextIndex]
    }
    setProjectId(prev => {
      const newId = getId(prev)
      pushProjectToUrl(newId)

      return newId
    })
  }

  useEffect(() => {
    const handlePopstate = () => {
      const url = new URL(window.location.href)
      const project = url.searchParams.get('project') || undefined
      const isProjectExist = projects.find(({ id }) => project === id)
      setProjectId(isProjectExist ? project : undefined)
    }
    window.addEventListener('popstate', handlePopstate)

    return () => {
      window.removeEventListener('popstate', handlePopstate)
    }
  }, [projects, setProjectId])

  return { switchProject, seeMore, close, projectId }
}
